// src/pages/FileVideoPage.tsx
// Standalone video viewer for /Dashboard/FileVideo/#/[videoId]
// Reads the video ID from the URL hash, validates permissions, and shows
// the video player — no MainLayout, no extra controls, left-click disabled.

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { apiService } from '../services/api';
import { downloadFile, fetchAttachmentBytes, base64ToBytes } from '../services/imagesGalleryService';
import { useVideoDecodeFallback } from '../components/Globals/useVideoDecodeFallback';
import VideoTranscodeOverlay from '../components/Globals/VideoTranscodeOverlay';
import './FileVideoPage.css';

interface FilePermissionsResponse {
  HasPermissions: boolean;
  Id: number;
  IsReply: boolean;
}

interface FileVideoItem {
  AttachmentId: number;
  EmbeddedImageId: number | null;
  FileName: string;
  FileUrl: string;
  FileExtension: string;
  Description: string;
  Title?: string;
}

export const FileVideoPage: React.FC = () =>
{
  const location = useLocation();

  // Hash: "#/{id}" for conversation videos, "#/Chat/{fileName}" for chat videos
  const rawHash = location.hash.replace(/^#\/?/, '').trim();
  const isFromChat = /^chat\//i.test(rawHash);
  const videoId = isFromChat ? rawHash.replace(/^chat\//i, '').trim() : rawHash;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [videoItem, setVideoItem] = useState<FileVideoItem | null>(null);

  const videoRef = useRef<HTMLVideoElement>(null);

  // Swaps in an H.264 copy when the browser cannot decode the original.
  // The source is read through the API: storage does not allow CORS.
  const loadSource = useCallback(async (): Promise<Uint8Array> =>
  {
    if (!videoItem) throw new Error('No video loaded');

    if (isFromChat)
    {
      const data = await apiService.get<string>(`Chat/GetFileChat?chatAttachmentId=${videoItem.AttachmentId}`);
      return base64ToBytes(data);
    }

    return fetchAttachmentBytes(videoItem.AttachmentId, videoItem.EmbeddedImageId);
  }, [videoItem, isFromChat]);

  const { src, transcodeProgress, transcodeFailed, handlers: decodeHandlers } =
    useVideoDecodeFallback(videoItem?.FileUrl ?? '', videoRef, videoItem ? loadSource : undefined);

  useEffect(() =>
  {
    if (!videoId)
    {
      setError('No video ID provided.');
      setLoading(false);
      return;
    }

    const loadVideo = async () =>
    {
      setLoading(true);
      setError(null);
      try
      {
        // 1. Validate permissions (chat videos require the isFromChat flag)
        const perms = await apiService.get<FilePermissionsResponse>(
          `Video/GetUrlFileVideoByPermissions?fileName=${videoId}${isFromChat ? '&isFromChat=true' : ''}`,
        );

        if (!perms.HasPermissions)
        {
          setError('You do not have permission to view this video.');
          return;
        }

        // 2. Fetch video data
        if (isFromChat)
        {
          // Returns a single object, not an array
          const chatVideo = await apiService.get<FileVideoItem>(
            `Chat/GetFileImages?fileName=${videoId}`,
          );

          if (!chatVideo || !chatVideo.FileUrl)
          {
            setError('Video not found.');
            return;
          }

          setVideoItem(chatVideo);
          return;
        }

        const videos = await apiService.get<FileVideoItem[]>(
          `Conversation/GetFileImages?id=${videoId}&IsReply=${perms.IsReply}`,
        );

        if (!videos || videos.length === 0)
        {
          setError('Video not found.');
          return;
        }

        setVideoItem(videos[0] ?? null);
      }
      catch (err)
      {
        console.error('FileVideoPage: failed to load video', err);
        setError('Failed to load video. Please make sure you are logged in.');
      }
      finally
      {
        setLoading(false);
      }
    };

    loadVideo();
  }, [videoId, isFromChat]);

  const handleDownload = useCallback(
    async (e: React.MouseEvent) =>
    {
      e.preventDefault();
      if (!videoItem) return;
      const fileName =
        (videoItem.Description || videoItem.FileName || 'video') + (videoItem.FileExtension || '');
      try
      {
        await downloadFile(
          videoItem.AttachmentId,
          videoItem.EmbeddedImageId ?? 0,
          fileName,
          videoItem.FileUrl,
        );
      }
      catch (err)
      {
        console.error('FileVideoPage: download failed', err);
      }
    },
    [videoItem],
  );

  if (loading)
  {
    return (
      <div className="fv-page">
        <div className="fv-spinner" />
      </div>
    );
  }

  if (error || !videoItem)
  {
    return (
      <div className="fv-page fv-page--error">
        <i className="material-icons fv-error-icon">videocam_off</i>
        <p className="fv-error-text">{error || 'Video not available.'}</p>
      </div>
    );
  }

  return (
    <div className="fv-page">
      <div className="fv-card">
        {/* Video player */}
        <div className="fv-player-wrapper">
          <video
            ref={videoRef}
            className="fv-player"
            src={src}
            controls={!transcodeProgress && !transcodeFailed}
            controlsList="nodownload"
            disablePictureInPicture
            autoPlay
            onContextMenu={e => e.preventDefault()}
            {...decodeHandlers}
          />

          <VideoTranscodeOverlay
            transcodeProgress={transcodeProgress}
            transcodeFailed={transcodeFailed}
          />
        </div>

        {/* Footer: description + download */}
        <div className="fv-footer">
          <span className="fv-description">
            {videoItem.Description || videoItem.FileName || ''}
          </span>
          <a href="#" className="fv-download-link" onClick={handleDownload}>
            Download
          </a>
        </div>
      </div>
    </div>
  );
};

export default FileVideoPage;
